import { Injectable, signal } from '@angular/core';
import { claveDePreferencia, guardarPreferencia, leerPreferencia } from 'nebula-ui-kit';

/**
 * Quien decide si la aplicacion se ve clara u oscura.
 *
 * Existe porque la decision estaba tomada en tres sitios -el pie de carga, la
 * pantalla de entrada y el armazon-, cada uno leyendo el almacenamiento por su
 * cuenta y pintando el documento por su cuenta. Con tres copias, un cambio de turno
 * repintaba unas partes y otras no.
 *
 * El tema es una preferencia DEL USUARIO, no de la maquina: en una terminal por
 * turnos cada empleado tiene el suyo. Y mientras nadie ha entrado -la pantalla de
 * entrada- no hay preferencia que valga, asi que se respeta la del sistema
 * operativo, que es lo que esa persona ya eligio para todo lo demas.
 */
@Injectable({ providedIn: 'root' })
export class ThemeService {
  /** Prefijo con el que el tema se guarda entre las preferencias del usuario. */
  private static readonly PREFIJO = 'nebula_tema_';

  /** Nombre propio de esta preferencia dentro del espacio del usuario. */
  private static readonly NOMBRE = 'oscuro';

  /** Clase que el documento lleva cuando la aplicacion se ve oscura. */
  private static readonly CLASE_OSCURA = 'dark-theme';

  /** Si la aplicacion se esta viendo oscura. */
  readonly oscuro = signal<boolean>(false);

  /**
   * Pone el tema que corresponde a quien esta dentro.
   *
   * Se llama al arrancar y, sobre todo, al entrar y al salir un usuario: la
   * aplicacion no se recarga al cambiar de turno, de modo que sin esto el empleado
   * que entra seguiria viendo el tema del que se fue.
   */
  aplicar(): void {
    this.fijar(this.preferido());
  }

  /** Cambia al otro tema y lo deja recordado a nombre de quien esta dentro. */
  alternar(): void {
    const siguiente = !this.oscuro();
    this.fijar(siguiente);
    guardarPreferencia(this.clave(), siguiente);
  }

  /**
   * El tema que toca: el del usuario si lo tiene elegido, y si no el del sistema.
   *
   * Un usuario que nunca ha tocado el mando no tiene preferencia, y entonces manda
   * su sistema operativo; no se le impone el claro por defecto.
   */
  private preferido(): boolean {
    const guardado = leerPreferencia<boolean>(this.clave());
    return guardado ?? this.delSistema();
  }

  /** Lo que el sistema operativo tiene puesto, o claro si no sabe decirlo. */
  private delSistema(): boolean {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  }

  /** Nombre con el que este usuario guarda su tema, o nulo si no hay nadie dentro. */
  private clave(): string | null {
    return claveDePreferencia(ThemeService.PREFIJO, ThemeService.NOMBRE);
  }

  /** Pinta el documento y deja constancia, sin guardar nada. */
  private fijar(oscuro: boolean): void {
    this.oscuro.set(oscuro);
    if (typeof document === 'undefined') return;
    document.documentElement.classList.toggle(ThemeService.CLASE_OSCURA, oscuro);
  }
}
